import React, { useState, useEffect, useMemo } from 'react';
import { UserCircle } from 'lucide-react';
import reportService from '../../services/report/reportService';
import api from '../../services/api';
import { formatCurrency } from '../../utils/currencyUtils';
import UserReportFilters, { UserReportMode } from './UserReportFilters';
import UserReportFirmTotalsCard from './UserReportFirmTotalsCard';
import UserReportUserCard from './UserReportUserCard';
import type { UserReportData, UserReportRequest, UserReportUser } from '../../types/report';

const UserReport: React.FC = () => {
  const [startDate, setStartDate] = useState<string>(
    new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0]
  );
  const [endDate, setEndDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [mode, setMode] = useState<UserReportMode>('all_with_house');
  const [userId, setUserId] = useState<number | undefined>(undefined);
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [downloadingExcel, setDownloadingExcel] = useState(false);
  const [reportData, setReportData] = useState<UserReportData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [expandedUsers, setExpandedUsers] = useState<Set<number>>(new Set());
  const [printingUser, setPrintingUser] = useState<number | null>(null);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const response = await api.getEmployees();
      setUsers(response.data || []);
    } catch (err) {
      console.error('Failed to load users:', err);
    }
  };

  /**
   * Single source of truth for report request params. Used by Generate,
   * Print, and Excel handlers so the three exports always agree on mode
   * + filters. The per-user Print PDF in the result table is a special
   * case that always forces include_house=false (single-salesman handouts
   * must not surface House) — see handlePrintUserPdf.
   */
  const buildReportParams = (): UserReportRequest => ({
    start_date: startDate,
    end_date: endDate,
    user_id: mode === 'specific_user' ? userId : undefined,
    include_house: mode !== 'all_without_house',
  });

  const handleGenerateReport = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await reportService.generateUserReport(buildReportParams());
      setReportData(response.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  const handlePrintPdf = async () => {
    setLoading(true);
    try {
      await reportService.printUserPdf(buildReportParams());
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to print PDF');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadExcel = async () => {
    setDownloadingExcel(true);
    try {
      await reportService.downloadUserExcel(buildReportParams());
    } catch (err: any) {
      setError(err.message || 'Failed to download Excel');
    } finally {
      setDownloadingExcel(false);
    }
  };

  const handlePrintUserPdf = async (user: UserReportUser) => {
    try {
      setPrintingUser(user.user_id);
      // Per-salesman handouts always exclude House regardless of the
      // page-level mode — Peter doesn't want House lines on individual
      // payout printouts.
      await reportService.printUserPdf({
        start_date: startDate,
        end_date: endDate,
        user_id: user.user_id,
        include_house: false,
      });
    } catch (err: any) {
      console.error('Failed to print user report:', err);
      setError(err.response?.data?.message || 'Failed to print user report');
    } finally {
      setPrintingUser(null);
    }
  };

  const toggleUserExpansion = (userIdToToggle: number) => {
    setExpandedUsers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(userIdToToggle)) {
        newSet.delete(userIdToToggle);
      } else {
        newSet.add(userIdToToggle);
      }
      return newSet;
    });
  };

  // Transform users to options format for SearchableSelect
  const userOptions = useMemo(() => {
    return users.map(user => ({
      id: user.id,
      label: user.name || user.email || 'Unnamed User'
    }));
  }, [users]);

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center">
          <UserCircle className="h-7 w-7 mr-3 text-primary-600" />
          User Commission Report
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          Generate commission report for employees by date range
        </p>
      </div>

      <UserReportFilters
        startDate={startDate}
        endDate={endDate}
        mode={mode}
        userId={userId}
        userOptions={userOptions}
        loading={loading}
        downloadingExcel={downloadingExcel}
        showExportButtons={!!reportData}
        error={error}
        onStartDateChange={setStartDate}
        onEndDateChange={setEndDate}
        onModeChange={setMode}
        onUserIdChange={setUserId}
        onGenerate={handleGenerateReport}
        onPrintPdf={handlePrintPdf}
        onDownloadExcel={handleDownloadExcel}
      />

      {/* Loading State */}
      {loading && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12">
          <div className="flex flex-col items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mb-4"></div>
            <p className="text-gray-600">Generating report...</p>
          </div>
        </div>
      )}

      {/* Report Data */}
      {!loading && reportData && (
        <div className="space-y-6">
          <UserReportFirmTotalsCard
            summary={reportData.summary}
            dateRange={reportData.date_range}
          />

          {/* Users */}
          {reportData.users.map((user) => (
            <UserReportUserCard
              key={user.user_id}
              user={user}
              expanded={expandedUsers.has(user.user_id)}
              onToggle={() => toggleUserExpansion(user.user_id)}
              onPrint={() => handlePrintUserPdf(user)}
              isPrinting={printingUser === user.user_id}
              formatCurrency={formatCurrency}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default UserReport;
